import { authService } from '../auth/authService';

// Base API URL from environment variable
// In development, use relative path to leverage Vite proxy
// In production, use full URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 
  (import.meta.env.DEV ? '/api' : 'http://localhost:8000/api');

/**
 * Get authentication headers for API requests
 */
export const getAuthHeaders = (): Record<string, string> => {
  const token = authService.getToken();
  if (!token) {
    throw new Error('No authentication token found. Please log in again.');
  }
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * API Error class for better error handling
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Handle API response and errors
 */
const handleApiResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
    
    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorMessage;
    } catch (e) {
      // If JSON parsing fails, use the status text
    }
    
    // Handle specific status codes
    if (response.status === 401) {
      // Token expired - logout user
      authService.logout();
      throw new ApiError('Authentication expired. Please log in again.', 401, 'Unauthorized');
    } else if (response.status === 403) {
      throw new ApiError('Access denied. You do not have permission to access this resource.', 403, 'Forbidden');
    } else if (response.status === 404) {
      throw new ApiError('Resource not found.', 404, 'Not Found');
    } else if (response.status >= 500) {
      throw new ApiError('Server error. Please try again later.', response.status, response.statusText);
    } else {
      throw new ApiError(errorMessage, response.status, response.statusText);
    }
  }
  
  return await response.json();
};

/**
 * Fetch wrapper with authentication and error handling
 */
export const apiFetch = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...options.headers
      }
    });
    
    return await handleApiResponse(response);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    } else if (error instanceof TypeError && error.message.includes('fetch')) {
      // Network error - backend not running
      throw new ApiError(
        'Unable to connect to the server. Please make sure the backend is running.',
        0,
        'Network Error'
      );
    } else {
      throw new ApiError(
        `Request failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        0,
        'Request Error'
      );
    }
  }
};

/**
 * GET request helper
 */
export const apiGet = (
  endpoint: string,
  queryParams?: Record<string, string | number | boolean | undefined | null>,
): Promise<any> => {
  let url = endpoint;
  
  if (queryParams) {
    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    url += `?${searchParams.toString()}`;
  }
  
  return apiFetch(url, { method: 'GET' });
};

/**
 * POST request helper
 */
export const apiPost = (endpoint: string, data?: any): Promise<any> => {
  return apiFetch(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined
  });
};

/**
 * PUT request helper
 */
export const apiPut = (endpoint: string, data?: any): Promise<any> => {
  return apiFetch(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined
  });
};

/**
 * DELETE request helper
 */
export const apiDelete = (endpoint: string): Promise<any> => {
  return apiFetch(endpoint, { method: 'DELETE' });
};

export { API_BASE_URL };

